"use client"

import { useEffect, useState } from "react"
import { Package, Truck, CheckCircle, Clock, X, Search, MoreVertical } from "lucide-react"
import { postData } from "../../services/NodeServices"
import { toast } from "react-toastify"
import { useNavigate } from "react-router-dom"

// Order Card Component
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message }) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
        <h3 className="text-lg font-medium text-slate-600 mb-2">{title}</h3>
        <p className="text-sm text-slate-500 mb-4">{message}</p>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm text-white bg-orange-500 rounded hover:bg-orange-600"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  )
}

// Dropdown Menu Component
const DropdownMenu = ({ isOpen, onClose, order, onAction }) => {
  if (!isOpen) return null

  const canCancel = order.status.toLowerCase() === "accepted" || order.status.toLowerCase() === "in transit"
  const canReturn = order.status.toLowerCase() === "delivered"

  return (
    <div className="absolute right-0 top-8 bg-white border border-slate-200 rounded-lg shadow-lg z-10 min-w-32">
      {canCancel && (
        <button
          onClick={() => onAction("cancel",order.orderid)}
          className="w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 first:rounded-t-lg"
        >
          Cancel Order
        </button>
      )}
      {canReturn && (
        <button
          onClick={() => onAction("return",order.orderid)}
          className="w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 last:rounded-b-lg"
        >
          Return Order
        </button>
      )}
      {!canCancel && !canReturn && <div className="px-3 py-2 text-sm text-slate-400">No actions available</div>}
    </div>
  )
}

// Order Card Component
const OrderCard = ({ order }) => {
  const [showDropdown, setShowDropdown] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [selectedAction, setSelectedAction] = useState(null)

 

  const getStatusColor = (status) => {
    const statusLower = status.toLowerCase()
    switch (statusLower) {
      case "delivered":
        return "text-green-600"
      case "in transit":
        return "text-orange-500"
      case "shipped":
        return "text-orange-500"
      case "accepted":
        return "text-orange-500"
      case "cancel requested":
        return "text-red-500"
      default:
        return "text-slate-600"
    }
  }

  const getStatusIcon = (status) => {
    const statusLower = status.toLowerCase()
    switch (statusLower) {
      case "delivered":
        return <CheckCircle size={12} />
      case "in transit":
        return <Truck size={12} />
      case "shipped":
        return <Truck size={12} />
      case "accepted":
        return <Clock size={12} />
      case "cancel requested":
        return <X size={12} />
      default:
        return <Package size={12} />
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  }

  const getImageUrl = (imagePath) => {
    return imagePath ? `/placeholder.svg?height=60&width=60&query=${imagePath}` : "/placeholder.svg?height=60&width=60"
  }

  const handleAction = (action,orderid) => {
    setSelectedAction(action)

   window.location.href = `cancel-order?orderId=${orderid}&action=${action}`;
    
    setShowDropdown(false)
    setShowModal(true)
  }

  const handleConfirm = () => {
    // Handle the actual action here (API call, etc.)
    console.log(`${selectedAction} order #${order.orderid}`)
    setShowModal(false)
    setSelectedAction(null)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedAction(null)
  }

  const subtotal = order.amount - order.shippingfees + order.discount

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 relative">
      {/* Order Header */}
      <div className="flex items-center justify-between mb-3 pb-3 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-slate-600">Order #{order.orderid}</span>
          <div className={`flex items-center gap-1 ${getStatusColor(order.status)}`}>
            {getStatusIcon(order.status)}
            <span className="text-xs font-medium capitalize">{order.status}</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-xs text-slate-500">{formatDate(order.created_at)}</div>
            <div className="text-sm font-medium text-black">₹{order.amount}</div>
          </div>
          <div className="relative">
            <button onClick={() => setShowDropdown(!showDropdown)} className="p-1 hover:bg-slate-100 rounded">
              <MoreVertical size={16} className="text-slate-400" />
            </button>
            <DropdownMenu
              isOpen={showDropdown}
              onClose={() => setShowDropdown(false)}
              order={order}
              onAction={handleAction}
            />
          </div>
        </div>
      </div>

      {/* Product List */}
      <div className="space-y-3 mb-3">
        {order.productlist && order.productlist.length > 0 ? (
          order.productlist.map((item, index) => (
            <div key={index} className="flex items-center gap-3">
              <div className="w-12 h-12 bg-slate-50 rounded border border-slate-200 flex items-center justify-center overflow-hidden">
                <img
                  src={getImageUrl(item.image_url) || "/placeholder.svg"}
                  alt={item.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm text-left font-medium text-black truncate">{item.name}</h4>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span>Size: {item.size}</span>
                  {item.color && (
                    <div className="flex items-center gap-1">
                      <div
                        className="w-2 h-2 rounded-full border border-slate-300"
                        style={{ backgroundColor: item.color }}
                      ></div>
                    </div>
                  )}
                  <span>Qty: {item.quantity || 1}</span>
                </div>
              </div>
              <span className="text-sm font-medium text-black">₹{item.price}</span>
            </div>
          ))
        ) : (
          <div className="text-center py-4 text-slate-500">
            <Package size={20} className="mx-auto mb-1 opacity-50" />
            <span className="text-xs">No products found</span>
          </div>
        )}
      </div>

      {/* Order Summary */}
      <div className="text-xs text-slate-500 mb-3">
        <div className="flex justify-between">
          <span>Subtotal:</span>
          <span>₹{subtotal}</span>
        </div>
        {order.discount > 0 && (
          <div className="flex justify-between text-green-600">
            <span>Discount:</span>
            <span>-₹{order.discount}</span>
          </div>
        )}
        {order.shippingfees > 0 && (
          <div className="flex justify-between">
            <span>Shipping:</span>
            <span>₹{order.shippingfees}</span>
          </div>
        )}
      </div>

      {/* Order Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
        <div className="text-xs text-slate-500">
          <div>Ship to: {order.customername}</div>
          <div>
            {order.city}, {order.state} - {order.zipcode}
          </div>
        </div>
        <div className="flex gap-2">
          <button className="text-xs text-slate-600 border border-slate-200 px-3 py-1 rounded hover:bg-slate-50">
            View Invoice
          </button>
          <button className="text-xs text-white bg-orange-500 px-3 py-1 rounded hover:bg-orange-600">View Order</button>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirm}
        title={selectedAction === "cancel" ? "Cancel Order" : "Return Order"}
        message={`Are you sure you want to ${selectedAction} order #${order.orderid}?`}
      />
    </div>
  )
}

// Header Component
const OrderHeader = ({ activeFilter, onFilterChange, searchQuery, onSearchChange, orders }) => {
  const getFilterCounts = () => {
    const counts = {
      all: orders.length,
      accepted: 0,
      "in transit": 0,
      delivered: 0,
      "cancel requested": 0,
    }

    orders.forEach((order) => {
      const status = order.status.toLowerCase()
      if (counts.hasOwnProperty(status)) {
        counts[status]++
      }
    })

    return counts
  }

  const filterCounts = getFilterCounts()
  const filters = [
    { key: "all", label: "All Orders", count: filterCounts.all },
    { key: "accepted", label: "Processing", count: filterCounts.accepted },
    { key: "in transit", label: "In Transit", count: filterCounts["in transit"] },
    { key: "delivered", label: "Delivered", count: filterCounts.delivered },
    { key: "cancel requested", label: "Cancelled", count: filterCounts["cancel requested"] },
  ]

  return (
    <div className="bg-white border-b border-slate-200 ">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-semibold text-slate-600">Your Orders</h1>
            <p className="text-sm text-slate-500">Track and manage your purchases</p>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={14} />
            <input
              type="text"
              placeholder="Search orders..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-8 pr-3 py-2 w-64 border border-slate-200 rounded text-sm focus:border-orange-500 focus:outline-none"
            />
          </div>
        </div>

        <div className="flex gap-2">
          {filters.map((filter) => (
            <button
              key={filter.key}
              onClick={() => onFilterChange(filter.key)}
              className={`px-3 py-1 rounded text-sm font-medium ${
                activeFilter === filter.key
                  ? "bg-orange-500 text-white"
                  : "text-slate-600 hover:text-orange-500 hover:bg-orange-50"
              }`}
            >
              {filter.label}
              <span
                className={`ml-1 px-1 rounded text-xs ${
                  activeFilter === filter.key ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"
                }`}
              >
                {filter.count}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// Main Order List Page Component
const OrderListPage = () => {
  const [activeFilter, setActiveFilter] = useState("all")
  const [searchQuery, setSearchQuery] = useState("")

  const [trackingData, setTrackingData] = useState({});
 
  const [orders,setOrders]=useState([])

  const navigate = useNavigate()




  const handleReturnOrder = async (orderId) => {

  navigate(`/return-order?orderId=${orderId}&step=1`); 

}

const handleCancelOrder = async (orderId) => {

  navigate(`/cancel-order?orderId=${orderId}&action=cancel`);

}

const handleRequestHelp = async (orderId) => {
  navigate(`/cancel-order?orderId=${orderId}&action=request-help`);
}


  const fetchTrackingData = async (orders) => {
      const trackingResults = {};
      for (let order of orders) {
        if (order.waybill) {
          try {
            const trackingResult = await postData("api/order/tracking", {
              waybill: order.waybill,
            });
            trackingResults[order.waybill] =
              trackingResult.data || "No tracking data found";
          } catch {
            trackingResults[order.waybill] = "Tracking unavailable";
          }
        }
      }
  
      setTrackingData(trackingResults);
  
      console.log("Tracking Data:", trackingResults);
  
      console.log();
    };
  

  
    const getOrderList = async () => {
      try {
        let userid = JSON.parse(localStorage.getItem("user")).userid;
        const result = await postData("api/order/byid", { userid: userid });
  
        if (result.status) {
          setOrders(result.data);
  
          fetchTrackingData(result.data);
        } else {
        }
      } catch {
        toast.error("failed to get user");
      }
    };
    useEffect(() => {
      getOrderList();
    }, []);
 

  // Filter orders
  const filteredOrders = orders.filter((order) => {
    const matchesFilter = activeFilter === "all" || order.status.toLowerCase() === activeFilter.toLowerCase()
    const matchesSearch =
      order.orderid.toString().includes(searchQuery) ||
      order.productlist.some((item) => item.name.toLowerCase().includes(searchQuery.toLowerCase()))
    return matchesFilter && matchesSearch
  })

  return (
    <div className="min-h-screen bg-white mt-[71px] relative pt-16">
      <OrderHeader
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        orders={orders}
      />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {filteredOrders.map((order) => (
          <OrderCard key={order.orderid} order={order} />
        ))}

        {filteredOrders.length === 0 && (
          <div className="text-center py-12">
            <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center mx-auto mb-3">
              <Package className="h-6 w-6 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-600 mb-1">No orders found</h3>
            <p className="text-sm text-slate-500">Try adjusting your search or filter criteria.</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default OrderListPage
